import { AppHTTPService } from '@/api/http-service'
import { FieldsContext } from '@/contexts/FieldsContext.tsx'
import { useSheet } from '@/contexts/SheetContext.tsx'
import { useCallback, useContext, useEffect, useState } from 'react'
import { sendConversationalMessage } from '../api/sse/prepare-chat'
import { connectionManager } from '../api/sse/prepare-chat/connection-manager'
import {
  ActiveTool,
  AgentStatus,
  ChatItem,
  OperationItem,
  PrepareChatConfig,
} from './types.ts'
import { useChatMessages } from './useChatMessages.tsx'
import { useEventProcessing } from './useEventProcessing.tsx'
import { useThreadController } from './useThreadController.tsx'
import { useOperationsController } from './useoperationsController.tsx'

/**
 * Main hook that coordinates all prepare chat functionality
 */
export const usePrepareChatTask = (
  config?: PrepareChatConfig
): {
  chatItems: ChatItem[]
  isLoading: boolean
  initStatus: AgentStatus
  threadId: string | null
  activeTools: Record<string, ActiveTool>
  pendingRules: any[]
  resetPendingRules: () => void
  handleStartPrepareChat: (instructions: string) => void
  disableInteractiveComponents: () => void
  handleOptionClick: (option: any) => void
  optionsDisplayed: boolean
  optionsDisabled: boolean
  handleStop: () => void
  operationItems: OperationItem[]
  createOrGetThread: () => Promise<string>
  sendMessageToThread: (
    threadId: string,
    content: string,
    role?: string,
    additionalData?: Record<string, any>
  ) => Promise<void>
  addRevertOperation: (
    threadId: string,
    targetOperationId: string
  ) => Promise<any>
  statusText: string
} => {
  // Compose the smaller hooks
  const { sheet } = useSheet()
  const { columnConfigWithState: columnConfig } = useContext(FieldsContext)

  const showAssistantMessages = true

  const {
    threadId,
    status: initStatus,
    isLoading,
    setIsLoading,
    createOrGetThread,
    handleStop,
  } = useThreadController(config)
  const {
    chatItems,
    addChatItem,
    updateChatItem,
    sendMessageToThread,
    optionsDisplayed,
    setOptionsDisplayed,
    optionsDisabled,
    setOptionsDisabled,
    statusText,
    setStatusText,
    removeChatItem,
  } = useChatMessages(threadId, config, showAssistantMessages)

  const {
    operationItems,
    updateOperation,
    addRevertOperation,
    pendingRules,
    resetPendingRules,
    updateRulesFromState,
  } = useOperationsController(threadId, config)

  const {
    // connectToThreadStream,
    connectToProjectStream,
    threadStreamConnectedRef,
    projectStreamConnectedRef,
  } = useEventProcessing({
    threadId,
    projectId: config?.projectId,
    setStatus: (status) => status,
    setIsLoading,
    addChatItem,
    removeChatItem,
    updateChatItem,
    updateOperation,
    updateRulesFromState,
    setStatusText,
    showAssistantMessages,
  })

  // For backward compatibility, maintain activeTools state
  const [activeTools, setActiveTools] = useState<Record<string, ActiveTool>>({})

  // // Connect to thread stream as soon as we have a threadId
  // useEffect(() => {
  //   let unsubscribeFunc: (() => void) | null = null

  //   if (threadId && !threadStreamConnectedRef.current.connected) {
  //     connectToThreadStream(threadId)
  //       .then((unsubscribe) => {
  //         unsubscribeFunc = unsubscribe
  //         console.log(`Successfully connected to thread stream: ${threadId}`)
  //       })
  //       .catch((err) =>
  //         console.error(`Failed to connect to thread stream: ${err.message}`)
  //       )
  //   }

  //   // Check connection at a less frequent interval to reduce resource usage
  //   const checkInterval = setInterval(() => {
  //     if (threadId && !threadStreamConnectedRef.current.connected) {
  //       console.log(
  //         `Thread stream disconnected, attempting to reconnect: ${threadId}`
  //       )
  //       connectToThreadStream(threadId)
  //         .then((unsubscribe) => {
  //           // Clean up previous unsubscribe if it exists
  //           if (unsubscribeFunc) {
  //             unsubscribeFunc()
  //           }
  //           unsubscribeFunc = unsubscribe
  //           console.log(`Reconnected to thread stream: ${threadId}`)
  //         })
  //         .catch((err) =>
  //           console.error(
  //             `Failed to reconnect to thread stream: ${err.message}`
  //           )
  //         )
  //     }
  //   }, 30000) // Check every 30 seconds (reduced from 10s to decrease connections)

  //   // Return cleanup function
  //   return () => {
  //     clearInterval(checkInterval)
  //     if (unsubscribeFunc) {
  //       unsubscribeFunc()
  //       console.log(`Cleaned up thread stream connection: ${threadId}`)
  //     }
  //   }
  // }, [threadId, connectToThreadStream, threadStreamConnectedRef])

  // Initialize thread when component mounts - but don't connect yet
  // The connection will happen in the other useEffect that handles connection management
  useEffect(() => {
    async function initializeThread() {
      // Only initialize if we have a sourceSheetId but no threadId yet
      if (!threadId && config?.sourceSheetId) {
        try {
          // Just create the thread, don't connect to it yet
          await createOrGetThread()
          console.log(
            'Thread initialized, connection will be managed by primary effect'
          )
        } catch (error) {
          console.error('Error initializing thread:', error)
        }
      }
    }

    initializeThread()
  }, [threadId, config?.sourceSheetId, createOrGetThread])

  /**
   * Disables interactive components (e.g., options buttons)
   */
  const disableInteractiveComponents = useCallback(() => {
    setOptionsDisabled(true)
  }, [setOptionsDisabled])

  /**
   * Handles starting a prepare chat session
   */
  const handleStartPrepareChat = useCallback(
    async (instructions: string) => {
      // When editing an artifact, we might not have a sourceSheetId
      if (!config?.sourceSheetId && !config?.artifactId) {
        console.error(
          'Source sheet ID is required when not editing an artifact'
        )
        return
      }

      try {
        setIsLoading(true)

        // Get or create the thread
        const currentThreadId = await createOrGetThread()

        // IMPORTANT: Always force a new connection for the thread
        // Close any existing connection first to ensure a clean state
        if (connectionManager.isConnected(currentThreadId)) {
          connectionManager.closeResource(currentThreadId)
          // Wait for the connection to fully close
          await new Promise((resolve) => setTimeout(resolve, 300))
        }

        // await connectToThreadStream(currentThreadId)

        // Verify that we're connected to the project stream if needed
        if (
          config?.projectId &&
          (!projectStreamConnectedRef.current.connected ||
            projectStreamConnectedRef.current.projectId !== config.projectId)
        ) {
          await connectToProjectStream(config.projectId)
        }

        // Brief delay to ensure connection is fully established
        await new Promise((resolve) => setTimeout(resolve, 300))

        // Add the user message to the thread
        await sendMessageToThread(currentThreadId, instructions)

        // Another brief delay to ensure message is processed
        await new Promise((resolve) => setTimeout(resolve, 300))

        // Send conversational message that can trigger prepare tasks
        await sendConversationalMessage(
          currentThreadId,
          instructions,
          config?.sourceSheetId
        )
      } catch (error) {
        console.error('Error starting prepare chat:', error)
        setIsLoading(false)
      }
    },
    [
      config?.sourceSheetId,
      config?.projectId,
      createOrGetThread,
      connectToProjectStream,
      sendMessageToThread,
      setIsLoading,
      threadStreamConnectedRef,
      projectStreamConnectedRef,
      columnConfig,
    ]
  )

  /**
   * Handles option selection from an options message
   */
  const handleOptionClick = useCallback(
    async (option: any) => {
      const userMessageContent = `I'd like to ${option.action.toLowerCase()}`

      setIsLoading(true)

      try {
        const currentThreadId = threadId || (await createOrGetThread())
        // IMPORTANT: Always connect to the thread stream FIRST
        // await connectToThreadStream(currentThreadId)

        // Find the message that contains these options
        const messageWithOptions = chatItems.find(
          (item) =>
            item.type === 'options' &&
            item.options?.some((opt) => opt.id === option.id)
        )

        if (messageWithOptions) {
          // Update the message in state to mark this option as selected
          updateChatItem(messageWithOptions.id, {
            content: {
              text:
                typeof messageWithOptions.content === 'string'
                  ? messageWithOptions.content
                  : messageWithOptions.content.text,
              selected: option.id,
            },
            options: messageWithOptions.options?.map((opt) => ({
              ...opt,
              disabled: true,
            })),
          })

          // Update the message in the database to mark the option as selected
          try {
            if (messageWithOptions.id && threadId) {
              // TODO: OLD Message Update API
              await AppHTTPService.client.messages.update(
                threadId,
                messageWithOptions.id.replace('-options', ''),
                {
                  content: {
                    text:
                      typeof messageWithOptions.content === 'string'
                        ? messageWithOptions.content
                        : messageWithOptions.content.text,
                    selected: option.id,
                  },
                }
              )
            }
          } catch (error) {
            console.error('Error updating message with selected option:', error)
          }
        }

        await sendMessageToThread(currentThreadId, userMessageContent)

        // Use the sheet ID from the options message, not the currently selected sheet
        const sheetIdToUse =
          messageWithOptions?.sheetId || config?.sourceSheetId

        await sendConversationalMessage(
          currentThreadId,
          userMessageContent,
          sheetIdToUse
        )
      } catch (error) {
        console.error('Error handling option click:', error)
        setIsLoading(false)
      }
    },
    [
      threadId,
      chatItems,
      createOrGetThread,
      updateChatItem,
      sendMessageToThread,
      setIsLoading,
      config?.threadId,
      config?.sourceSheetId,
      columnConfig,
    ]
  )

  useEffect(() => {
    if (sheet?.metadata?.analysis) {
      const analysis = sheet.metadata.analysis[0]
      addChatItem({
        id: 'initial-analysis',
        type: 'sheet-analysis',
        content: analysis.content,
        role: 'assistant',
        timestamp: new Date(analysis.createdAt).getTime(),
      })
    }
  }, [config?.sourceSheetId, sheet])

  return {
    chatItems,
    isLoading,
    initStatus,
    threadId,
    activeTools,
    pendingRules,
    resetPendingRules,
    handleStartPrepareChat,
    disableInteractiveComponents,
    handleOptionClick,
    optionsDisplayed,
    optionsDisabled,
    handleStop,
    operationItems,
    createOrGetThread,
    sendMessageToThread,
    addRevertOperation,
    statusText,
  }
}
